import type { ApiClient } from '../../../shared/api/api-client';
import type { BookingAddonRow, BookingAttendance, MyBookingRow, StaffSlotBookingRow } from './bookings.types';

function asBool(v: unknown, fallback: boolean): boolean {
  if (typeof v === 'boolean') return v;
  return fallback;
}

function asStr(v: unknown): string {
  return typeof v === 'string' ? v : '';
}

function asNum(v: unknown): number {
  const n = typeof v === 'number' ? v : Number(v);
  return Number.isFinite(n) ? n : NaN;
}

function normalizeBookingAddonRow(raw: Record<string, unknown>): BookingAddonRow | null {
  const id = asNum(raw.id);
  const booking_id = asNum(raw.booking_id);
  const addon_service_id = asNum(raw.addon_service_id);
  if (!Number.isFinite(id) || id < 1) return null;
  if (!Number.isFinite(booking_id) || booking_id < 1) return null;
  if (!Number.isFinite(addon_service_id) || addon_service_id < 1) return null;
  return {
    id,
    booking_id,
    addon_service_id,
    addon_name: asStr(raw.addon_name),
    price_cents: Number.isFinite(Number(raw.price_cents)) ? Number(raw.price_cents) : 0,
    base_price_cents: Number.isFinite(Number(raw.base_price_cents)) ? Number(raw.base_price_cents) : 0,
    price_overridden: asBool(raw.price_overridden, false),
    is_required: asBool(raw.is_required, false),
    status: asStr(raw.status).trim() || 'active',
    added_by: raw.added_by == null ? null : Number(raw.added_by),
    covered_sub_id: raw.covered_sub_id == null ? null : Number(raw.covered_sub_id),
    created_at: asStr(raw.created_at)
  };
}

function normalizeBookingAddons(raw: unknown): { addons: BookingAddonRow[]; total: number } {
  if (!Array.isArray(raw)) return { addons: [], total: 0 };
  const addons: BookingAddonRow[] = [];
  let total = 0;
  for (const row of raw) {
    if (!row || typeof row !== 'object' || Array.isArray(row)) continue;
    const n = normalizeBookingAddonRow(row as Record<string, unknown>);
    if (n) {
      addons.push(n);
      total += n.price_cents;
    }
  }
  return { addons, total };
}

function normalizeMyBookingRow(raw: Record<string, unknown>): MyBookingRow | null {
  const id = asNum(raw.id);
  const slot_id = asNum(raw.slot_id);
  const service_id = asNum(raw.service_id);
  if (!Number.isFinite(id) || id < 1) return null;
  if (!Number.isFinite(slot_id) || slot_id < 1) return null;
  if (!Number.isFinite(service_id) || service_id < 1) return null;
  const status = asStr(raw.status).trim() || 'unknown';
  const created_at = asStr(raw.created_at).trim();
  const slot_date = asStr(raw.slot_date).trim();
  const start_time = asStr(raw.start_time).trim();
  const end_time = asStr(raw.end_time).trim();
  const service_name = asStr(raw.service_name).trim();
  if (!created_at || !slot_date) return null;
  const payment_method =
    raw.payment_method === null || raw.payment_method === undefined
      ? null
      : typeof raw.payment_method === 'string'
        ? raw.payment_method
        : null;
  const { addons, total: addonsTotal } = normalizeBookingAddons(raw.addons);
  return {
    id,
    slot_id,
    service_id,
    status,
    created_at,
    slot_date,
    start_time,
    end_time,
    service_name,
    can_cancel: asBool(raw.can_cancel, false),
    payment_method,
    service_payment_required: asBool(raw.service_payment_required, true),
    payment_received: asBool(raw.payment_received, false),
    addons,
    addons_total_cents: addonsTotal
  };
}

function asNullableStr(v: unknown): string | null {
  if (v === null || v === undefined) return null;
  return typeof v === 'string' ? v : null;
}

function asNullableNum(v: unknown): number | null {
  if (v === null || v === undefined) return null;
  const n = typeof v === 'number' ? v : Number(v);
  return Number.isFinite(n) && n > 0 ? n : null;
}

function normalizeStaffSlotBookingRow(raw: Record<string, unknown>): StaffSlotBookingRow | null {
  const base = normalizeMyBookingRow(raw);
  if (!base) return null;
  const client_id = asNum(raw.client_id);
  if (!Number.isFinite(client_id) || client_id < 1) return null;
  const av = raw.avatar_url;
  const avatar_url =
    av === null || av === undefined
      ? undefined
      : typeof av === 'string' && av.trim()
        ? av.trim()
        : undefined;
  return {
    ...base,
    client_id,
    client_name: asNullableStr(raw.client_name),
    comment: asNullableStr(raw.comment),
    represented_minor_id: asNullableNum(raw.represented_minor_id),
    represented_minor_name: asNullableStr(raw.represented_minor_name),
    slot_branch_id: asNullableNum(raw.slot_branch_id),
    user_subscription_id: asNullableNum(raw.user_subscription_id),
    avatar_url,
    attendance: raw.attendance === 'attended' || raw.attendance === 'no_show' ? raw.attendance : null,
    qr_entered_at: asNullableStr(raw.qr_entered_at),
    managed_by: raw.managed_by === 'yclients' ? 'yclients' : raw.managed_by === 'crm' ? 'crm' : null,
    yclients_id: asNullableStr(raw.yclients_id),
  };
}

/** `GET /bookings` возвращает JSON-массив (см. `widgetHandler.listBookings`). */
export async function fetchMyBookingsList(client: ApiClient): Promise<MyBookingRow[]> {
  const raw = await client.request<unknown>('/bookings', { method: 'GET' });
  if (!Array.isArray(raw)) return [];
  const out: MyBookingRow[] = [];
  for (const row of raw) {
    if (!row || typeof row !== 'object' || Array.isArray(row)) continue;
    const n = normalizeMyBookingRow(row as Record<string, unknown>);
    if (n) out.push(n);
  }
  return out;
}

export async function postCancelMyBooking(client: ApiClient, bookingId: number): Promise<void> {
  await client.request<unknown>(`/bookings/${bookingId}/cancel`, {
    method: 'POST',
    body: {}
  });
}

/** `POST /bookings/{id}/attendance` — отметка прихода клиента (null — снять отметку). */
export async function postBookingAttendance(
  client: ApiClient,
  params: { organization_id: number; booking_id: number; attendance: BookingAttendance | null }
): Promise<void> {
  const sp = new URLSearchParams();
  sp.set('organization_id', String(params.organization_id));
  await client.request<unknown>(`/bookings/${params.booking_id}/attendance?${sp.toString()}`, {
    method: 'POST',
    body: { attendance: params.attendance }
  });
}

export async function postConfirmCashBooking(client: ApiClient, bookingId: number): Promise<void> {
  await client.request<unknown>(`/bookings/${bookingId}/confirm-cash`, {
    method: 'POST',
    body: {}
  });
}

export interface BookingListResponse {
  items: StaffSlotBookingRow[];
  total: number;
}

export async function fetchClientsBookings(
  client: ApiClient,
  params: {
    limit?: number;
    offset?: number;
    status?: string;
    q?: string;
    branch_id?: number;
    from_date?: string;
    to_date?: string;
  }
): Promise<BookingListResponse> {
  const sp = new URLSearchParams();
  if (params.limit != null) sp.set('limit', String(params.limit));
  if (params.offset != null) sp.set('offset', String(params.offset));
  if (params.status) sp.set('status', params.status);
  if (params.q) sp.set('q', params.q);
  if (params.branch_id != null) sp.set('branch_id', String(params.branch_id));
  if (params.from_date) sp.set('from_date', params.from_date);
  if (params.to_date) sp.set('to_date', params.to_date);
  const raw = await client.request<unknown>(`/admin/bookings?${sp.toString()}`, { method: 'GET' });
  if (!raw || typeof raw !== 'object' || Array.isArray(raw)) return { items: [], total: 0 };
  const body = raw as Record<string, unknown>;
  const itemsArr = Array.isArray(body.items) ? body.items : [];
  const items: StaffSlotBookingRow[] = [];
  for (const row of itemsArr) {
    if (!row || typeof row !== 'object' || Array.isArray(row)) continue;
    const n = normalizeStaffSlotBookingRow(row as Record<string, unknown>);
    if (n) items.push(n);
  }
  const total = typeof body.total === 'number' ? body.total : items.length;
  return { items, total };
}

export type StaffSlotBookingsResult = {
  items: StaffSlotBookingRow[];
  total: number;
  /** Тренер смотрит чужое занятие: сервер отдал только количество, без имён/телефонов. */
  masked: boolean;
};

export async function fetchStaffSlotBookingsResult(
  client: ApiClient,
  params: { organization_id: number; slot_id: number }
): Promise<StaffSlotBookingsResult> {
  const sp = new URLSearchParams();
  sp.set('organization_id', String(params.organization_id));
  sp.set('slot_id', String(params.slot_id));
  const raw = await client.request<unknown>(`/bookings/for-slot?${sp.toString()}`, { method: 'GET' });
  const body = raw && typeof raw === 'object' && !Array.isArray(raw) ? (raw as Record<string, unknown>) : null;
  const items = body && Array.isArray(body.items) ? body.items : Array.isArray(raw) ? raw : [];
  const out: StaffSlotBookingRow[] = [];
  for (const row of items) {
    if (!row || typeof row !== 'object' || Array.isArray(row)) continue;
    const n = normalizeStaffSlotBookingRow(row as Record<string, unknown>);
    if (n) out.push(n);
  }
  const total = body && typeof body.total === 'number' ? body.total : out.length;
  const masked = body != null && body.masked === true;
  return { items: out, total, masked };
}

export async function fetchStaffSlotBookings(
  client: ApiClient,
  params: { organization_id: number; slot_id: number }
): Promise<StaffSlotBookingRow[]> {
  const result = await fetchStaffSlotBookingsResult(client, params);
  return result.items;
}

export async function createStaffBookingForClient(
  client: ApiClient,
  params: {
    organization_id: number;
    slot_id: number;
    client_id: number;
    comment?: string | null;
    /** Для детских групп: карточка ребёнка (обязательна для group_type=child). */
    represented_minor_id?: number | null;
  }
): Promise<StaffSlotBookingRow | null> {
  const sp = new URLSearchParams();
  sp.set('organization_id', String(params.organization_id));
  const body: Record<string, unknown> = {
    slot_id: params.slot_id,
    client_id: params.client_id,
    comment: params.comment?.trim() ? params.comment.trim() : null,
  };
  if (params.represented_minor_id != null && params.represented_minor_id > 0) {
    body.represented_minor_id = params.represented_minor_id;
  }
  const raw = await client.request<unknown>(`/bookings/for-client?${sp.toString()}`, {
    method: 'POST',
    body,
  });
  if (!raw || typeof raw !== 'object' || Array.isArray(raw)) return null;
  return normalizeStaffSlotBookingRow(raw as Record<string, unknown>);
}

export type StaffBookingPatch = {
  status?: 'pending' | 'confirmed' | 'cancelled';
  comment?: string | null;
  represented_minor_id?: number | null;
};

export async function patchStaffBooking(
  client: ApiClient,
  params: { organization_id: number; booking_id: number; patch: StaffBookingPatch }
): Promise<StaffSlotBookingRow | null> {
  const sp = new URLSearchParams();
  sp.set('organization_id', String(params.organization_id));
  const raw = await client.request<unknown>(`/bookings/${params.booking_id}?${sp.toString()}`, {
    method: 'PATCH',
    body: params.patch,
  });
  if (!raw || typeof raw !== 'object' || Array.isArray(raw)) return null;
  return normalizeStaffSlotBookingRow(raw as Record<string, unknown>);
}

export async function deleteStaffBooking(
  client: ApiClient,
  params: { organization_id: number; booking_id: number }
): Promise<void> {
  const sp = new URLSearchParams();
  sp.set('organization_id', String(params.organization_id));
  await client.request<unknown>(`/bookings/${params.booking_id}?${sp.toString()}`, { method: 'DELETE' });
}

export type AllowedAddonOption = {
  id: number;
  name: string;
  price: number;
  branch_id: number | null;
  is_required: boolean;
};

/** GET /services/{id}/addons — допы, разрешённые для основной услуги. */
export async function fetchAllowedAddons(
  client: ApiClient,
  params: { organization_id: number; service_id: number; branch_id?: number | null }
): Promise<AllowedAddonOption[]> {
  const sp = new URLSearchParams();
  sp.set('organization_id', String(params.organization_id));
  if (params.branch_id != null && params.branch_id > 0) sp.set('branch_id', String(params.branch_id));
  const raw = await client.request<unknown>(`/services/${params.service_id}/addons?${sp.toString()}`, {
    method: 'GET'
  });
  if (!raw || typeof raw !== 'object' || Array.isArray(raw)) return [];
  const items = (raw as Record<string, unknown>).items;
  if (!Array.isArray(items)) return [];
  const out: AllowedAddonOption[] = [];
  for (const row of items) {
    if (!row || typeof row !== 'object' || Array.isArray(row)) continue;
    const r = row as Record<string, unknown>;
    const id = typeof r.id === 'number' ? r.id : Number(r.id);
    if (!Number.isFinite(id) || id < 1) continue;
    out.push({
      id,
      name: typeof r.name === 'string' ? r.name : `#${id}`,
      price: Number.isFinite(Number(r.price)) ? Number(r.price) : 0,
      branch_id: r.branch_id == null ? null : Number(r.branch_id),
      is_required: Boolean(r.is_required)
    });
  }
  return out;
}

/** POST /bookings/{id}/addons — добавить доп (staff: ручная цена price_cents разрешена). */
export async function addBookingAddonStaff(
  client: ApiClient,
  params: {
    organization_id: number;
    booking_id: number;
    addon_service_id: number;
    price_cents?: number | null;
    payment_method?: 'online' | 'cash' | 'subscription';
  }
): Promise<{ payment_url?: string | null }> {
  const sp = new URLSearchParams();
  sp.set('organization_id', String(params.organization_id));
  const body: Record<string, unknown> = { addon_service_id: params.addon_service_id };
  if (params.price_cents != null) body.price_cents = params.price_cents;
  if (params.payment_method) body.payment_method = params.payment_method;
  const raw = await client.request<unknown>(`/bookings/${params.booking_id}/addons?${sp.toString()}`, {
    method: 'POST',
    body
  });
  if (!raw || typeof raw !== 'object' || Array.isArray(raw)) return {};
  return raw as { payment_url?: string | null };
}

/** DELETE /bookings/{id}/addons/{addonId} — убрать доп. */
export async function removeBookingAddonStaff(
  client: ApiClient,
  params: { organization_id: number; booking_id: number; addon_service_id: number }
): Promise<void> {
  const sp = new URLSearchParams();
  sp.set('organization_id', String(params.organization_id));
  await client.request<unknown>(
    `/bookings/${params.booking_id}/addons/${params.addon_service_id}?${sp.toString()}`,
    { method: 'DELETE' }
  );
}

export type BookingTransactionItem = {
  id: number;
  kind: string;
  direction: string;
  source: string;
  provider: string | null;
  amount_cents: number;
  created_at: string | null;
};

export type BookingPriceSnapshotItem = {
  id: number;
  kind: string;
  price_cents: number;
  payment_source: string;
  status: string;
  refunded_cents: number;
  is_trial: boolean;
};

/**
 * История движений и снимков брони для UI «Связи» (ADR-0041):
 * `GET /bookings/{id}/transactions` (append-only журнал + снимки цен).
 */
export async function fetchBookingTransactions(
  client: ApiClient,
  params: { organization_id: number; booking_id: number }
): Promise<{ items: BookingTransactionItem[]; snapshots: BookingPriceSnapshotItem[] }> {
  const sp = new URLSearchParams();
  sp.set('organization_id', String(params.organization_id));
  const raw = await client.request<unknown>(`/bookings/${params.booking_id}/transactions?${sp.toString()}`, {
    method: 'GET'
  });
  if (!raw || typeof raw !== 'object' || Array.isArray(raw)) return { items: [], snapshots: [] };
  const o = raw as Record<string, unknown>;
  const items: BookingTransactionItem[] = [];
  if (Array.isArray(o.items)) {
    for (const row of o.items) {
      if (!row || typeof row !== 'object' || Array.isArray(row)) continue;
      const r = row as Record<string, unknown>;
      const id = typeof r.id === 'number' ? r.id : Number(r.id);
      if (!Number.isFinite(id) || id < 1) continue;
      items.push({
        id,
        kind: typeof r.kind === 'string' ? r.kind : '',
        direction: typeof r.direction === 'string' ? r.direction : '',
        source: typeof r.source === 'string' ? r.source : '',
        provider: typeof r.provider === 'string' ? r.provider : null,
        amount_cents: typeof r.amount_cents === 'number' ? r.amount_cents : 0,
        created_at: typeof r.created_at === 'string' ? r.created_at : null
      });
    }
  }
  const snapshots: BookingPriceSnapshotItem[] = [];
  if (Array.isArray(o.snapshots)) {
    for (const row of o.snapshots) {
      if (!row || typeof row !== 'object' || Array.isArray(row)) continue;
      const r = row as Record<string, unknown>;
      const id = typeof r.id === 'number' ? r.id : Number(r.id);
      if (!Number.isFinite(id) || id < 1) continue;
      snapshots.push({
        id,
        kind: typeof r.kind === 'string' ? r.kind : '',
        price_cents: typeof r.price_cents === 'number' ? r.price_cents : 0,
        payment_source: typeof r.payment_source === 'string' ? r.payment_source : '',
        status: typeof r.status === 'string' ? r.status : '',
        refunded_cents: typeof r.refunded_cents === 'number' ? r.refunded_cents : 0,
        is_trial: r.is_trial === true
      });
    }
  }
  return { items, snapshots };
}
